import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, unref as _unref, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { style: {"display":"flex","flex-direction":"column","gap":"20px"} }
const _hoisted_2 = { class: "panel" }
const _hoisted_3 = { class: "panel-body" }
const _hoisted_4 = { class: "donut-wrap" }
const _hoisted_5 = { class: "dlegend" }
const _hoisted_6 = { class: "dleg-l" }
const _hoisted_7 = { class: "dleg-v" }
const _hoisted_8 = { class: "panel" }
const _hoisted_9 = { class: "panel-body" }
const _hoisted_10 = { class: "bar-list" }
const _hoisted_11 = ["title"]
const _hoisted_12 = { class: "bar-track" }
const _hoisted_13 = ["data-pct"]
const _hoisted_14 = { class: "bar-n" }
const _hoisted_15 = { class: "panel" }
const _hoisted_16 = {
  class: "panel-body",
  style: {"padding":"0 20px"}
}
const _hoisted_17 = { class: "agent-list" }
const _hoisted_18 = {
  key: 1,
  class: "empty",
  style: {"padding":"20px"}
}
const _hoisted_19 = ["onClick"]
const _hoisted_20 = ["src", "alt", "onMouseenter", "onError"]
const _hoisted_21 = ["onMouseenter"]
const _hoisted_22 = { class: "agent-info" }
const _hoisted_23 = { class: "agent-n" }
const _hoisted_24 = {
  key: 0,
  class: "skel skel-sm"
}
const _hoisted_25 = {
  key: 1,
  class: "agent-e"
}
const _hoisted_26 = { class: "conv-count" }
const _hoisted_27 = {
  key: 0,
  class: "agents-pagination agents-pagination--compact"
}
const _hoisted_28 = ["disabled"]
const _hoisted_29 = {
  class: "ap-info",
  style: {"margin-left":"0"}
}
const _hoisted_30 = ["disabled"]

import { ref, computed, watch, nextTick } from 'vue'
import type { Conversation, PopupData } from '@/types/dashboard'
import { avatarColor, initials } from '@/composables/useHelpers'
import { profileLabel } from '@/composables/useAdmin'

const AGENT_PAGE_SIZE = 12

export default /*@__PURE__*/_defineComponent({
  __name: 'SidePanel',
  props: {
    conversations: {},
    agentTeamsMap: {},
    scopeReady: { type: Boolean }
  },
  emits: ["showPopup", "hidePopup", "openAgentSummary"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit  = __emit

const donutEl = ref<HTMLCanvasElement | null>(null)

// Donut segments
const donutSegs = computed(() => {
  const c = props.conversations
  const open     = c.filter(x => x.status === 'open').length
  const resolved = c.filter(x => x.status === 'resolved').length
  const pending  = c.filter(x => x.status === 'pending').length
  const other    = c.length - open - resolved - pending
  return [
    { label: 'Aberta',    value: open,     color: '#0369A1' },
    { label: 'Resolvida', value: resolved, color: '#1F8C5A' },
    { label: 'Pendente',  value: pending,  color: '#FF6600' },
    { label: 'Outro',     value: other,    color: '#DDDCDC' },
  ].filter(s => s.value > 0)
})

// Bar items
const barItems = computed(() => {
  const map: Record<string, number> = {}
  props.conversations.forEach(c => {
    const k = c.team?.name || 'TypeBot'
    map[k] = (map[k] || 0) + 1
  })
  const sorted = Object.entries(map).sort((a, b) => b[1] - a[1])
  const max = sorted[0]?.[1] || 1
  return sorted.map(([name, count]) => ({ name, count, pct: count / max }))
})

// Agent items
const agentItems = computed(() => {
  const map: Record<number, any> = {}
  props.conversations.forEach(c => {
    if (!c.assignee) return
    const k = c.assignee.id
    if (!map[k]) map[k] = { ...c.assignee, count: 0, teams: new Set<string>() }
    map[k].count++
    if (c.team?.name) map[k].teams.add(c.team.name)
  })
  return Object.values(map).sort((a, b) => b.count - a.count)
})

// Paginação — mesmo PAGE_SIZE de AgentsTab.vue (aba "Agentes" com os cards),
// pra não sobrar espaço vazio numa lista enquanto a outra pagina diferente.
const agentPage = ref(1)

const agentTotalPages = computed(() => Math.ceil(agentItems.value.length / AGENT_PAGE_SIZE))

const pagedAgentItems = computed(() => {
  const start = (agentPage.value - 1) * AGENT_PAGE_SIZE
  return agentItems.value.slice(start, start + AGENT_PAGE_SIZE)
})

watch(agentItems, () => { agentPage.value = 1 })

// Draw donut
watch(donutSegs, drawDonut, { immediate: false })
function drawDonut() {
  const canvas = donutEl.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')!
  const W = canvas.width, H = canvas.height, cx = W / 2, cy = H / 2
  const R = W * 0.41, r = W * 0.25
  const segs  = donutSegs.value
  const total = segs.reduce((s, d) => s + d.value, 0)
  ctx.clearRect(0, 0, W, H)
  if (!total) {
    ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.arc(cx, cy, r, 0, Math.PI * 2, true)
    ctx.fillStyle = '#F0EFEF'; ctx.fill()
  } else {
    let angle = -Math.PI / 2
    segs.forEach(seg => {
      const sweep = (seg.value / total) * Math.PI * 2
      ctx.beginPath()
      ctx.moveTo(cx + r * Math.cos(angle), cy + r * Math.sin(angle))
      ctx.arc(cx, cy, R, angle, angle + sweep)
      ctx.arc(cx, cy, r, angle + sweep, angle, true)
      ctx.closePath()
      ctx.fillStyle = seg.color; ctx.fill()
      angle += sweep
    })
  }
  ctx.fillStyle = '#1A1A1A'; ctx.font = `800 ${W * .17}px Poppins,Arial`
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText(String(total), cx, cy - 4)
  ctx.fillStyle = '#6B6B6B'; ctx.font = `400 ${W * .08}px Poppins,Arial`
  ctx.fillText('total', cx, cy + H * .13)
}

// Animate bars
const barRefs: HTMLElement[] = []
function setBarRef(el: HTMLElement | null, i: number) {
  if (el) barRefs[i] = el
}

watch(barItems, async () => {
  await nextTick()
  barRefs.forEach((b, i) => {
    if (!b) return
    const pct = parseFloat(b.dataset.pct || '0')
    setTimeout(() => {
      b.style.transition = `transform .7s cubic-bezier(.4,0,.2,1) ${i * 60}ms`
      b.style.transform  = `scaleX(${pct})`
    }, 50)
  })
})

function handleImgError(e: Event, agent: any) {
  const img = e.target as HTMLImageElement
  const div = document.createElement('div')
  div.className = 'av'
  div.style.background = avatarColor(agent.name)
  div.style.cursor = 'pointer'
  div.textContent = initials(agent.name)
  div.addEventListener('mouseenter', ev => onAvatarEnter(agent, ev as MouseEvent))
  div.addEventListener('mouseleave', () => emit('hidePopup'))
  img.replaceWith(div)
}

function onAvatarEnter(agent: any, e: MouseEvent) {
  emit('showPopup', {
    name:      agent.name,
    role:      profileLabel(agent.id, props.agentTeamsMap?.[agent.id] ?? []),
    email:     agent.email || '',
    thumbnail: agent.thumbnail || '',
    status:    agent.availability_status || 'offline',
    convs:     agent.count,
    team:      [...agent.teams].join(', ') || null,
  }, e.currentTarget as HTMLElement)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[4] || (_cache[4] = _createStaticVNode("<div class=\"panel-head\"><div class=\"panel-title\"><svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\"><path d=\"M21.21 15.89A10 10 0 1 1 8 2.83\"></path><path d=\"M22 12A10 10 0 0 0 12 2v10z\"></path></svg> Status das Conversas </div></div>", 1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("canvas", {
            ref_key: "donutEl",
            ref: donutEl,
            width: "170",
            height: "170"
          }, null, 512),
          _createElementVNode("div", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(donutSegs.value, (s) => {
              return (_openBlock(), _createElementBlock("div", {
                key: s.label,
                class: "dleg-row"
              }, [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("div", {
                    class: "dleg-dot",
                    style: _normalizeStyle({ background: s.color })
                  }, null, 4),
                  _createTextVNode(" " + _toDisplayString(s.label), 1)
                ]),
                _createElementVNode("span", _hoisted_7, _toDisplayString(s.value), 1)
              ]))
            }), 128))
          ])
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_8, [
      _cache[5] || (_cache[5] = _createStaticVNode("<div class=\"panel-head\"><div class=\"panel-title\"><svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\"><rect x=\"3\" y=\"3\" width=\"18\" height=\"18\" rx=\"2\"></rect><line x1=\"9\" y1=\"9\" x2=\"9\" y2=\"15\"></line><line x1=\"12\" y1=\"7\" x2=\"12\" y2=\"15\"></line><line x1=\"15\" y1=\"11\" x2=\"15\" y2=\"15\"></line></svg> Conversas por Time </div></div>", 1)),
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("div", _hoisted_10, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(barItems.value, (item, i) => {
            return (_openBlock(), _createElementBlock("div", {
              key: item.name,
              class: "bar-row"
            }, [
              _createElementVNode("div", {
                class: "bar-lbl",
                title: item.name
              }, _toDisplayString(item.name), 9, _hoisted_11),
              _createElementVNode("div", _hoisted_12, [
                _createElementVNode("div", {
                  class: "bar-fill",
                  ref_for: true,
                  ref: el => setBarRef(el as HTMLElement, i),
                  "data-pct": item.pct
                }, null, 8, _hoisted_13)
              ]),
              _createElementVNode("div", _hoisted_14, _toDisplayString(item.count), 1)
            ]))
          }), 128))
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_15, [
      _cache[10] || (_cache[10] = _createStaticVNode("<div class=\"panel-head\"><div class=\"panel-title\"><svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\"><circle cx=\"12\" cy=\"8\" r=\"4\"></circle><path d=\"M20 21a8 8 0 1 0-16 0\"></path></svg> Agentes </div></div>", 1)),
      _createElementVNode("div", _hoisted_16, [
        _createElementVNode("div", _hoisted_17, [
          (__props.scopeReady === false)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, _renderList(3, (n) => {
                return _createElementVNode("div", {
                  key: `skel-${n}`,
                  class: "agent-row"
                }, [...(_cache[6] || (_cache[6] = [
                  _createElementVNode("div", { class: "skel skel-av" }, null, -1),
                  _createElementVNode("div", { class: "agent-info" }, [
                    _createElementVNode("div", {
                      class: "skel skel-md",
                      style: {"margin-bottom":"5px"}
                    }),
                    _createElementVNode("div", { class: "skel skel-sm" })
                  ], -1)
                ]))])
              }), 64))
            : (!agentItems.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_18, [...(_cache[7] || (_cache[7] = [
                  _createElementVNode("p", null, "Sem agentes", -1)
                ]))]))
              : (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(pagedAgentItems.value, (agent) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: agent.id,
                    class: "agent-row agent-row--clickable",
                    onClick: ($event: any) => (emit('openAgentSummary', agent, props.conversations.filter(c => c.assignee?.id === agent.id)))
                  }, [
                    (agent.thumbnail)
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          src: agent.thumbnail,
                          alt: agent.name,
                          class: "av-img",
                          onMouseenter: ($event: any) => (onAvatarEnter(agent, $event)),
                          onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('hidePopup'))),
                          onError: (e) => handleImgError(e, agent)
                        }, null, 40, _hoisted_20))
                      : (_openBlock(), _createElementBlock("div", {
                          key: 1,
                          class: "av",
                          style: _normalizeStyle({ background: _unref(avatarColor)(agent.name) }),
                          onMouseenter: ($event: any) => (onAvatarEnter(agent, $event)),
                          onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('hidePopup')))
                        }, _toDisplayString(_unref(initials)(agent.name)), 45, _hoisted_21)),
                    _createElementVNode("div", _hoisted_22, [
                      _createElementVNode("div", _hoisted_23, _toDisplayString(agent.name), 1),
                      (!__props.agentTeamsMap || !Object.keys(__props.agentTeamsMap).length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_24))
                        : (_openBlock(), _createElementBlock("div", _hoisted_25, _toDisplayString(_unref(profileLabel)(agent.id, __props.agentTeamsMap?.[agent.id] ?? [])), 1))
                    ]),
                    _createElementVNode("div", _hoisted_26, _toDisplayString(agent.count), 1)
                  ], 8, _hoisted_19))
                }), 128))
        ]),
        (agentTotalPages.value > 1)
          ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
              _createElementVNode("button", {
                class: "ap-btn",
                disabled: agentPage.value <= 1,
                onClick: _cache[2] || (_cache[2] = ($event: any) => (agentPage.value--)),
                title: "Página anterior"
              }, [...(_cache[8] || (_cache[8] = [
                _createElementVNode("svg", {
                  width: "14",
                  height: "14",
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  "stroke-width": "2.5",
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round"
                }, [
                  _createElementVNode("polyline", { points: "15 18 9 12 15 6" })
                ], -1)
              ]))], 8, _hoisted_28),
              _createElementVNode("span", _hoisted_29, _toDisplayString((agentPage.value - 1) * AGENT_PAGE_SIZE + 1) + "–" + _toDisplayString(Math.min(agentPage.value * AGENT_PAGE_SIZE, agentItems.value.length)) + " de " + _toDisplayString(agentItems.value.length), 1),
              _createElementVNode("button", {
                class: "ap-btn",
                disabled: agentPage.value >= agentTotalPages.value,
                onClick: _cache[3] || (_cache[3] = ($event: any) => (agentPage.value++)),
                title: "Próxima página"
              }, [...(_cache[9] || (_cache[9] = [
                _createElementVNode("svg", {
                  width: "14",
                  height: "14",
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  "stroke-width": "2.5",
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round"
                }, [
                  _createElementVNode("polyline", { points: "9 18 15 12 9 6" })
                ], -1)
              ]))], 8, _hoisted_30)
            ]))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})